import { toast } from 'sonner';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { useTenantStore } from '@/store/tenant.store';

export class SurveyDispatchIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    _err(error) {
        return getIntegrationErrorMessage(error);
    }

    async listByFormTemplate(formTemplateId) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: [], error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }
        try {
            const res = await this.service.listByFormTemplate(formTemplateId);
            return { success: true, data: res.data || [] };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, data: [], error: msg };
        }
    }

    async create(payload) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }
        try {
            const res = await this.service.create(payload);
            if (payload.scheduleMode === 'immediate' || res.run) {
                toast.success(
                    i18next.t('definitionsSurveys:messages.dispatchRunSuccess', {
                        defaultValue: 'Survey sent',
                        count: res.taskIds?.length || res.run?.taskIds?.length || 0,
                    }),
                );
            } else {
                toast.success(
                    i18next.t('definitionsSurveys:messages.dispatchCreateSuccess', {
                        defaultValue: 'Dispatch rule created',
                    }),
                );
            }
            return { success: true, data: res.data, run: res.run };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async update(id, payload) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }
        try {
            const res = await this.service.update(id, payload);
            if (payload.scheduleMode === 'immediate') {
                toast.success(
                    i18next.t('definitionsSurveys:messages.dispatchRunSuccess', {
                        defaultValue: 'Survey sent',
                        count: res.taskIds?.length || res.data?.taskIds?.length || 0,
                    }),
                );
            } else {
                toast.success(
                    i18next.t('definitionsSurveys:messages.dispatchUpdateSuccess', {
                        defaultValue: 'Dispatch rule updated',
                    }),
                );
            }
            return { success: true, data: res.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async remove(id) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }
        try {
            await this.service.delete(id);
            toast.success(
                i18next.t('definitionsSurveys:messages.dispatchDeleteSuccess', {
                    defaultValue: 'Dispatch rule removed',
                }),
            );
            return { success: true };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async runNow(id) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }
        try {
            const res = await this.service.runNow(id);
            toast.success(
                i18next.t('definitionsSurveys:messages.dispatchRunSuccess', {
                    defaultValue: 'Survey sent',
                    count: res.taskIds?.length || 0,
                }),
            );
            return { success: true, data: res.data, taskIds: res.taskIds };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }
}
